import { Spinner } from '@deskworks/ui';
import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router';
import { AcceptInvitePage, SetupPage, SignInPage } from '../auth/pages.js';
import { CallProvider } from '../meetings/call.js';
import { useSession } from './session.js';
import { AppShell } from './shell.js';

const MailPage = lazy(() => import('../mail/MailPage.js'));
const CalendarPage = lazy(() => import('../calendar/CalendarPage.js'));
const ChatPage = lazy(() => import('../chat/ChatPage.js'));
const MeetingsPage = lazy(() => import('../meetings/MeetingsPage.js'));
const PeoplePage = lazy(() => import('../people/PeoplePage.js'));
const FilesPage = lazy(() => import('../files/FilesPage.js'));
const ActivityPage = lazy(() => import('../activity/ActivityPage.js'));
const SearchPage = lazy(() => import('../search/SearchPage.js'));
const SettingsPage = lazy(() => import('../settings/SettingsPage.js'));
const MorePage = lazy(() => import('../settings/MorePage.js'));
const GuestJoinPage = lazy(() => import('../guest/GuestJoinPage.js'));
const DownloadPage = lazy(() => import('../download/DownloadPage.js'));
const RemotePage = lazy(() => import('../remote/RemotePage.js'));
const DownloadRemotePage = lazy(() => import('../remote/DownloadRemotePage.js'));

function Loading() {
  return (
    <div className="dw-loading">
      <Spinner size={28} label="Loading" />
    </div>
  );
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Loading />}>{children}</Suspense>;
}

/** Member boundary: everything below requires a signed-in member session. */
function RequireSession() {
  const { status } = useSession();
  const location = useLocation();
  if (status === 'loading') return <Loading />;
  if (status === 'signed-out') {
    const next = `${location.pathname}${location.search}`;
    return <Navigate to={`/signin?next=${encodeURIComponent(next)}`} replace />;
  }
  return (
    <CallProvider>
      <AppShell>
        <Lazy>
          <Outlet />
        </Lazy>
      </AppShell>
    </CallProvider>
  );
}

function AccountsReturn() {
  const { search } = useLocation();
  return <Navigate to={`/settings/accounts${search}`} replace />;
}

export const router = createBrowserRouter([
  { path: '/signin', element: <SignInPage /> },
  { path: '/setup', element: <SetupPage /> },
  { path: '/invite/:token', element: <AcceptInvitePage /> },
  { path: '/download', element: <Lazy><DownloadPage /></Lazy> },
  { path: '/download/remote', element: <Lazy><DownloadRemotePage /></Lazy> },
  // Restricted guest meeting route: separate access boundary, no member shell or data.
  {
    path: '/join/:code',
    element: (
      <Lazy>
        <GuestJoinPage />
      </Lazy>
    ),
  },
  {
    element: <RequireSession />,
    children: [
      { path: '/', element: <Navigate to="/mail" replace /> },
      { path: '/mail/*', element: <MailPage /> },
      { path: '/calendar/*', element: <CalendarPage /> },
      { path: '/chat', element: <ChatPage /> },
      { path: '/chat/:conversationId', element: <ChatPage /> },
      { path: '/meetings/*', element: <MeetingsPage /> },
      { path: '/remote/*', element: <RemotePage /> },
      { path: '/people', element: <PeoplePage /> },
      { path: '/people/:id', element: <PeoplePage /> },
      { path: '/files', element: <FilesPage /> },
      { path: '/activity', element: <ActivityPage /> },
      { path: '/search', element: <SearchPage /> },
      { path: '/settings', element: <SettingsPage /> },
      { path: '/settings/:section', element: <SettingsPage /> },
      // The desktop app's deskworks://accounts return link (after signing in to a provider in the browser).
      { path: '/accounts', element: <AccountsReturn /> },
      { path: '/more', element: <MorePage /> },
      { path: '*', element: <Navigate to="/mail" replace /> },
    ],
  },
]);

export function safeNext(next: string | null): string {
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.startsWith('/signin')) return '/mail';
  return next;
}

export { Outlet };
